<template>
  <div
    class="flex flex-col h-auto items-center justify-center pt-10 lg:pt-12 pb-10"
  >
    <!-- top header -->
    <div class="flex flex-col border rounded-lg w-full md:w-[85%] lg:w-[50%]">
      <div class="flex flex-col bg-subDark p-4 w-full">
        <div class="flex flex-row items-center justify-start">
          <svg
            width="20"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill-rule="evenodd"
            clip-rule="evenodd"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-message-square"
          >
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              ></path>
            </g>
          </svg>
          <p class="text-white text-lg font-bold pl-4">
            {{ $t("Live_Support_Chat") }}
          </p>
        </div>
        <div class="flex flex-row items-center justify-start pt-8">
          <div
            class="w-8 h-8 bg-white rounded-full flex items-center justify-center overflow-hidden"
          >
            <svg
              width="20"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill-rule="evenodd"
              clip-rule="evenodd"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <circle
                  cx="12"
                  cy="6"
                  r="4"
                  stroke="#1C274C"
                  stroke-width="1.5"
                ></circle>
                <path
                  d="M19.9975 18C20 17.8358 20 17.669 20 17.5C20 15.0147 16.4183 13 12 13C7.58172 13 4 15.0147 4 17.5C4 19.9853 4 22 12 22C14.231 22 15.8398 21.8433 17 21.5634"
                  stroke="#1C274C"
                  stroke-width="1.5"
                  stroke-linecap="round"
                ></path>
              </g>
            </svg>
          </div>
          <div class="flex flex-col">
            <p class="text-white text-base font-medium pl-4">
              {{ $t("Support_Team") }}
            </p>
            <p class="text-slate-300 text-sm font-normal pl-4">
              {{ $t("online-usually-responds-in-a-few-minutes") }}
            </p>
          </div>
        </div>
      </div>
      <!-- content section -->
      <div class="border w-full">
        <div class="flex flex-col-reverse space-y-4 h-96 overflow-y-auto px-1 md:px-6">
          <div
            v-if="isLoading"
            class="container w-full h-full flex flex-col items-center justify-center"
          >
            <img
              class="w-20 h-20 animate-spin fill-[#061B44]"
              src="../assets/icons/loading-loader.svg"
              alt="Loading icon"
            />
          </div>
          <div
            v-else-if="!isLoading && messagesList.length === 0"
            class="container w-full h-full flex flex-col items-center justify-center text-slate-500"
          >
            <p class="text-base md:text-lg font-medium text-center px-4">
              {{ $t("no-messages-yet-start-the-conversation-below") }}
            </p>
          </div>
          <div
            v-if="!isLoading && messagesList.length > 0"
            v-for="(message, index) in messagesList"
            :key="message.id"
          >
            <div
              :class="{ 'flex-row-reverse': message.isSentByUser }"
              class="flex flex-row space-x-1 md:space-x-2 justify-start pb-4"
            >
              <div v-if="!isSameSenderAsPrevious(index)" class="relative">
                <div
                  class="w-8 h-8 bg-gray-200 rounded-full overflow-hidden"
                ></div>

                <button
                  v-if="showDeleteIndex === index"
                  @click="deleteMessage(message.id)"
                  :disabled="isLoading"
                  class="absolute top-8 left-1/2 transform -translate-x-1/2 text-white text-xs font-bold py-1 px-2 rounded-md bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i class="pi pi-trash text-red-500 text-xl"></i>
                </button>
              </div>
              <div
                :class="{ 'items-end pr-2': message.isSentByUser }"
                class="flex flex-col w-[80%]"
              >
                <div v-if="!isSameSenderAsPrevious(index)">
                  <div
                    :class="{ 'flex-row-reverse': message.isSentByUser }"
                    class="flex flex-row items-center"
                  >
                    <p class="text-black text-sm md:text-base font-medium px-1">
                      {{
                        message.isSentByUser ? $t("You") : $t("Support_Team")
                      }}
                    </p>
                    <p class="text-gray-500 text-xs font-medium px-1">
                      {{ formatDate(message.date) }}
                    </p>
                  </div>
                </div>

                <p
                  @mouseover="showDeleteButton(index)"
                  @mouseleave="hideDeleteButton(index)"
                  class="text-white text-sm md:text-lg font-normal bg-subDark mix-w-max px-2 py-1 rounded-md"
                >
                  {{ message.content }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <!-- messaging area -->
        <div
          class="flex flex-row w-full items-center justify-center border py-4 space-x-2"
        >
          <textarea
            v-model="supportMessageInput"
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact.prevent="supportMessageInput += '\n'"
            :placeholder="$t('Type_a_message')"
            class="text-field h-10 w-full lg:w-[85%] text-start text-sm md:text-base rounded-md pt-2 pl-4 outline-none font-normal border"
            outlined
            single-line
            dense
            hide-details
            rows="1"
            auto-grow
          ></textarea>
          <button
            @click="sendMessage"
            :disabled="isLoading"
            class="h-10 bg-slate-500 rounded-md px-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              width="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill-rule="evenodd"
              clip-rule="evenodd"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M19.2111 2.06722L3.70001 5.94499C1.63843 6.46039 1.38108 9.28612 3.31563 10.1655L8.09467 12.3378C9.07447 12.7831 10.1351 12.944 11.1658 12.8342C11.056 13.8649 11.2168 14.9255 11.6622 15.9053L13.8345 20.6843C14.7139 22.6189 17.5396 22.3615 18.055 20.3L21.9327 4.78886C22.3437 3.14517 20.8548 1.6563 19.2111 2.06722ZM8.92228 10.517C9.85936 10.943 10.9082 10.9755 11.8474 10.6424C12.2024 10.5165 12.5417 10.3383 12.8534 10.1094C12.8968 10.0775 12.9397 10.0446 12.982 10.0108L15.2708 8.17974C15.6351 7.88831 16.1117 8.36491 15.8202 8.7292L13.9892 11.018C13.9553 11.0603 13.9225 11.1032 13.8906 11.1466C13.6617 11.4583 13.4835 11.7976 13.3576 12.1526C13.0244 13.0918 13.057 14.1406 13.4829 15.0777L15.6552 19.8567C15.751 20.0673 16.0586 20.0393 16.1147 19.8149L19.9925 4.30379C20.0372 4.12485 19.8751 3.96277 19.6962 4.00751L4.18509 7.88528C3.96065 7.94138 3.93264 8.249 4.14324 8.34473L8.92228 10.517Z"
                  fill="#ffffff"
                ></path>
              </g>
            </svg>
          </button>
        </div>
        <div class="flex flex-col md:flex-row md:justify-between space-y-1 md:space-y-0 px-1 md:px-4 h-11 md:h-8 items-center">
          <p class="text-slate-600 text-xs md:text-sm">{{ $t('need-help-were') }}</p>
          <div class="flex flex-row items-center space-x-2">
            <svg
            width="20"
            height="20"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              fill="#2563eb"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                <title></title>
                <g id="Complete">
                  <g id="mail">
                    <g>
                      <polyline
                        fill="none"
                        points="4 8.2 12 14.1 20 8.2"
                        stroke="#2563eb"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                      ></polyline>
                      <rect
                        fill="none"
                        height="14"
                        rx="2"
                        ry="2"
                        stroke="#2563eb"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        width="18"
                        x="3"
                        y="6.5"
                      ></rect>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
            <a
              href="mailto:support@subtitles.tech"
              class="text-blue-600 hover:underline text-sm md:text-base"
              >support@subtitles.tech</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useAuthStore } from "../store/auth";
import { API_BASE_URL, SUPPORTMESSAGES } from "../utils/appConst";
import "vue3-toastify/dist/index.css";
import { MessageModel } from "../utils/messageModel";
import { onMounted, computed } from "vue";
import { useUserActivities } from "../store/userActivities";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";

const authStore = useAuthStore();
const activityStore = useUserActivities();
const router = useRouter();
const isLoading = ref(false);
const { t } = useI18n();
const showDeleteIndex = ref<number | null>(null);
const hideTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const messagesList = ref<MessageModel[]>([]);
const supportMessagesUrl = `${API_BASE_URL}${SUPPORTMESSAGES}`;
let lastUserMessageId = "";
const supportMessageInput = computed({
  get: () => activityStore.supportMessageInput,
  set: (value) => (activityStore.supportMessageInput = value),
});
onMounted(() => {
  fetchMessages();
});

const fetchMessages = async () => {
  try {
    isLoading.value = true;
    const response = await fetch(supportMessagesUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    const result = await response.json();
    if (!response.ok) {
      isLoading.value = false;
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          toast(t("were-fixing-a-technical-issue-so-please-try"), {
            autoClose: 3000,
            type: "error",
            theme: "light",
          });
      }
      return;
    }
    if (result.message == "Success" && result.messages) {
      messagesList.value = messagesList.value.concat(
        result.messages.map((message: any) => ({
          id: message.id,
          content: message.content,
          date: message.createdAt,
          isSentByUser: message.senderIsUser,
        }))
      );
      const firstMessage = {
        id: "0",
        content: t("HelloHowcanIhelpyou"),
        date: "",
        isSentByUser: false,
      };
      messagesList.value.push(firstMessage);
      lastUserMessageId = messagesList.value[0].id;
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const deleteMessage = async (messageId: string) => {
  try {
    isLoading.value = true;
    const response = await fetch(supportMessagesUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ messageId: messageId }),
    });
    const result = await response.json();
    console.log(result);

    if (result.error) {
      isLoading.value = false;
      toast(t("were-fixing-a-technical-issue-so-please-try"), {
        autoClose: 3000,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (result.message == "Message deleted successfully") {
      messagesList.value = messagesList.value.filter(
        (message) => message.id !== messageId
      );
      lastUserMessageId = messagesList.value.length > 0 ? messagesList.value[0].id : "";
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const sendMessage = async (event: any) => {
  try {
    if (supportMessageInput.value.trim().length == 0) {
      return;
    }
    isLoading.value = true;
    const response = await fetch(supportMessagesUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ content: supportMessageInput.value.trim() }),
    });
    const result = await response.json();
    if (result.error) {
      isLoading.value = false;
      toast(t("were-fixing-a-technical-issue-so-please-try"), {
        autoClose: 3000,
        type: "error",
        theme: "light",
      });
      return;
    }
    console.log(result);

    if (result.message == "Success" && result.sentMessage) {
      messagesList.value.unshift({
        id: result.sentMessage.id,
        content: result.sentMessage.content,
        date: result.sentMessage.createdAt,
        isSentByUser: result.sentMessage.senderIsUser,
      });
    }
    supportMessageInput.value = "";
    lastUserMessageId = result.sentMessage.id;
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

function isSameSenderAsPrevious(index: number): boolean {
  if (index === 0 || !messagesList.value[index + 1]) {
    return false;
  }
  const result =
    messagesList.value[index]["isSentByUser"] ===
    messagesList.value[index + 1]["isSentByUser"];
  return result;
}

const showDeleteButton = (index: number) => {
  if (hideTimeout.value != null) clearTimeout(hideTimeout.value);
  showDeleteIndex.value = index;
};

const hideDeleteButton = (index: number) => {
  hideTimeout.value = setTimeout(() => {
    showDeleteIndex.value = null;
  }, 3000);
};

useHead({
  title:
    "Support - Subtitles Tech"
});
</script>
